import { datadogLogs } from '@datadog/browser-logs';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '@web-app/hooks/useApiClient';
import type { Data } from '@web-app/pages/api/healthie/appointment/status';
import { UPDATE_APPOINTMENT_STATUS_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import omit from 'lodash/omit';

import { SetAppointmentStatusBody } from './types';

const setAppointmentStatus = async (
  body: SetAppointmentStatusBody,
  apiClient: ReturnType<typeof useApiClient>,
): Promise<Data | undefined> => {
  // Use apiClient for the protected appointments endpoint
  await apiClient.put(`/appointments/${body.roomName}`, {
    status: body.status,
    healthieClientId: body.clientUserId,
    healthieProviderId: body.providerUserId,
    chartingSavedDirectlyInHealthie: body.chartingSavedDirectlyInHealthie,
  });

  if (body.appointmentId) {
    const appointmentResponse = await fetch(
      '/api/healthie/appointment/status',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...body,
          roomName: undefined,
        }),
      },
    );

    if (!appointmentResponse.ok) {
      datadogLogs.logger.error(
        `${UPDATE_APPOINTMENT_STATUS_LOG_PREFIX} Healthie appointment status update failed`,
        {
          appointmentId: body.appointmentId,
          statusCode: appointmentResponse.status,
          statusText: appointmentResponse.statusText,
        },
      );
      throw new Error('Failed to update appointment status');
    }

    return appointmentResponse.json();
  }

  return undefined;
};

export const useAppointmentStatus = () => {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: (body: SetAppointmentStatusBody) =>
      setAppointmentStatus(body, apiClient),
    retry: 2,
    retryDelay: 500,
    onSuccess: (data, variables) => {
      datadogLogs.logger.info(
        `${UPDATE_APPOINTMENT_STATUS_LOG_PREFIX} appointment status updated successfully`,
        {
          appointmentId: variables.appointmentId,
          roomName: variables.roomName,
          status: variables.status,
          data: omit(data, ['user']),
        },
      );
    },
    onError: (error, variables, context) => {
      datadogLogs.logger.error(
        `${UPDATE_APPOINTMENT_STATUS_LOG_PREFIX} appointment status update failed`,
        {
          appointmentId: variables.appointmentId,
          roomName: variables.roomName,
          status: variables.status,
          context,
          err:
            error instanceof Error
              ? {
                  message: error.message,
                  stack: error.stack,
                }
              : String(error),
        },
      );
    },
  });
};
